import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { api, ApiError, type JornadaDatesPayload, type JornadaDatesRow, type JornadaDatesSummary } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { daysBetween, jornadaRangeText } from "../lib/jornadaDates";

/** «Fechas de cada jornada» en el Calendario de un torneo SIN calendario de
 * canchas (Eduardo 2026-10-04): por categoría, cada jornada con «Del … al …»
 * editable. La sugerencia del formato (Inicio + «Días entre Jornadas») solo
 * se guarda cuando el organizador toca «Guardar». «Guardar y recorrer las
 * siguientes» mueve las siguientes los mismos días; «Usar estas fechas en
 * todas las categorías» copia cada jornada a la del mismo número en las
 * otras categorías. Con el torneo publicado, el servidor avisa a los
 * jugadores de esa jornada (uno por jugador). */

interface DivisionOption {
  id: number;
  name: string;
  format_type?: string | null;
}

export interface JornadaDatesPanelProps {
  token: string | null;
  tournamentId: number;
  divisions: DivisionOption[];
  /** Categoría abierta en el Calendario (?division=). */
  initialDivisionId: number | null;
  /** Permiso de la página (rol); el servidor además dice `can_edit`. */
  canEdit: boolean;
  /** Cambia cuando cambian los partidos (vuelve a leer las jornadas). */
  reloadKey?: string | number;
  /** Se guardaron fechas (Eduardo 2026-10-05: los PDF las usan sin recargar). */
  onSaved?: () => void;
  /** Pantalla única del Calendario: los partidos y descansos de cada jornada
   * van dentro de su renglón. */
  renderRowExtra?: (roundNumber: number) => ReactNode;
  /** Las jornadas que se pintaron (los partidos de las demás van aparte). */
  onRoundsLoaded?: (roundNumbers: number[]) => void;
}

interface Draft {
  starts: string;
  ends: string;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function shiftLabel(days: number): string {
  const sign = days > 0 ? "+" : "−";
  return `${sign}${plural(Math.abs(days), "día", "días")}`;
}

function savedWindow(row: JornadaDatesRow): Draft | null {
  return row.starts_on && row.ends_on ? { starts: row.starts_on, ends: row.ends_on } : null;
}

function suggestedWindow(row: JornadaDatesRow): Draft | null {
  return row.suggested_starts_on && row.suggested_ends_on
    ? { starts: row.suggested_starts_on, ends: row.suggested_ends_on }
    : null;
}

function noticeSentence(summary: JornadaDatesSummary | undefined): string {
  if (!summary) return "";
  if (!summary.notices_open) return " Sin aviso: los jugadores todavía no ven el torneo.";
  if (summary.notified_players > 0) {
    return ` Avisamos a ${plural(summary.notified_players, "jugador", "jugadores")}.`;
  }
  return "";
}

export default function JornadaDatesPanel({
  token,
  tournamentId,
  divisions,
  initialDivisionId,
  canEdit,
  reloadKey,
  onSaved,
  renderRowExtra,
  onRoundsLoaded,
}: JornadaDatesPanelProps) {
  const options = useMemo(
    () => divisions.filter((division) => division.format_type !== "elimination"),
    [divisions],
  );
  const [divisionId, setDivisionId] = useState<number | null>(
    initialDivisionId ?? options[0]?.id ?? null,
  );
  const { askConfirm } = useAbortableConfirm([tournamentId, divisionId]);
  useEffect(() => {
    // La categoría abierta en el Calendario manda; sin ella, la primera.
    setDivisionId((current) => {
      if (initialDivisionId !== null) return initialDivisionId;
      if (current !== null && options.some((division) => division.id === current)) return current;
      return options[0]?.id ?? null;
    });
  }, [initialDivisionId, options]);

  const [data, setData] = useState<JornadaDatesPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [busy, setBusy] = useState<number | "copy" | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!token) return;
    const request = ++requestRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.rounds.jornadaDates(token, tournamentId, divisionId);
      if (request !== requestRef.current) return;
      setData(response.data);
      setDrafts({});
    } catch (err) {
      if (request !== requestRef.current) return;
      setLoadError(err instanceof ApiError ? err.message || "No pudimos leer las jornadas." : "Error de red. Intenta otra vez.");
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [divisionId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const editable = canEdit && Boolean(data?.can_edit);
  const jornadas = data?.jornadas ?? [];
  const divisionName = data?.division_name ?? options.find((division) => division.id === divisionId)?.name ?? null;
  const hasSavedDates = jornadas.some((row) => savedWindow(row) !== null);
  const shownRounds = loadError || data?.needs_division ? "" : jornadas.map((row) => row.round_number).join(",");
  useEffect(() => {
    onRoundsLoaded?.(shownRounds ? shownRounds.split(",").map(Number) : []);
  }, [onRoundsLoaded, shownRounds]);

  function draftFor(row: JornadaDatesRow): Draft {
    return drafts[row.round_number] ?? savedWindow(row) ?? suggestedWindow(row) ?? { starts: "", ends: "" };
  }

  function updateDraft(row: JornadaDatesRow, patch: Partial<Draft>) {
    setDrafts((current) => ({ ...current, [row.round_number]: { ...draftFor(row), ...patch } }));
    setMessage(null);
    setActionError(null);
  }

  async function handleSave(row: JornadaDatesRow, shiftFollowing: boolean) {
    if (!token) return;
    const draft = draftFor(row);
    if (!draft.starts || !draft.ends || draft.ends < draft.starts) return;
    setBusy(row.round_number);
    setMessage(null);
    setActionError(null);
    try {
      const response = await api.rounds.setJornadaDates(token, tournamentId, {
        division_id: data?.division_id ?? divisionId,
        round_number: row.round_number,
        starts_on: draft.starts,
        ends_on: draft.ends,
        shift_following: shiftFollowing,
      });
      setData(response.data);
      setDrafts({});
      onSaved?.();
      const summary = response.data.summary;
      const range = jornadaRangeText(draft.starts, draft.ends);
      const following = (summary?.changed ?? []).filter((change) => change.round_number > row.round_number).length;
      const moved = shiftFollowing && following > 0 ? ` y ${plural(following, "jornada siguiente recorrida", "jornadas siguientes recorridas")}` : "";
      // «Jornada 3 guardada» / «Playoff 1 guardado».
      const saved = /^\s*playoff/i.test(row.label) ? "guardado" : "guardada";
      setMessage(`${row.label} ${saved}${range ? `: ${range}` : ""}${moved}.${noticeSentence(summary)}`);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message || "No pudimos guardar las fechas." : "Error de red. Intenta otra vez.");
    } finally {
      setBusy(null);
    }
  }

  async function handleCopyToAll() {
    if (!token || data?.division_id == null) return;
    const ok = await askConfirm(
      `¿Usar las fechas de las jornadas de ${divisionName ?? "esta categoría"} en las demás categorías? ` +
        "Cada jornada se copia a la jornada del mismo número, solo donde exista." +
        (data.notices_open ? " Los jugadores de esas jornadas reciben un aviso." : ""),
      { acceptLabel: "Sí, usar en todas" },
    );
    if (!ok) return;
    setBusy("copy");
    setMessage(null);
    setActionError(null);
    try {
      const response = await api.rounds.copyJornadaDatesToDivisions(token, tournamentId, data.division_id);
      setData(response.data);
      setDrafts({});
      onSaved?.();
      const summary = response.data.summary;
      const changed = summary?.changed.length ?? 0;
      setMessage(
        changed === 0
          ? "Las demás categorías ya tenían esas fechas (o no tienen esas jornadas)."
          : `Listo: ${plural(changed, "jornada actualizada", "jornadas actualizadas")} en ${plural(
              summary?.divisions_changed ?? 0,
              "categoría",
              "categorías",
            )}.${noticeSentence(summary)}`,
      );
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message || "No pudimos copiar las fechas." : "Error de red. Intenta otra vez.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card jornada-dates" data-jornada-dates>
      <div className="card__header-row">
        <div>
          <h2 className="card__title">Fechas de cada jornada{divisionName ? ` · ${divisionName}` : ""}</h2>
          <p className="card__subtitle">
            Los jugadores ven entre qué fechas se juega cada jornada en su próximo reto y en Juegos.
          </p>
        </div>
        {options.length > 1 ? (
          <label className="jornada-dates__division">
            <span>Categoría</span>
            <select className="t123-select"
              value={divisionId ?? ""}
              onChange={(event) => {
                setDivisionId(Number(event.target.value));
                setMessage(null);
                setActionError(null);
              }}
              disabled={busy !== null}
            >
              {options.map((division) => (
                <option key={division.id} value={division.id}>
                  {division.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {data ? (
        <p className="muted jornada-dates__notice" data-no-toast data-jornada-dates-notice>
          {!editable
            ? "Solo puedes consultar estas fechas."
            : data.notices_open
              ? "Al guardar, los jugadores de esa jornada reciben un aviso con las nuevas fechas (uno por jugador)."
              : "Los jugadores todavía no ven el torneo: puedes cambiar las fechas sin que les llegue ningún aviso."}
        </p>
      ) : null}

      {loadError ? (
        <p className="muted" role="alert" data-no-toast>
          {loadError}
        </p>
      ) : loading && !data ? (
        <p className="muted" data-no-toast>Cargando las jornadas…</p>
      ) : data?.needs_division ? (
        <p className="muted" data-no-toast>Elige una categoría para poner las fechas de sus jornadas.</p>
      ) : data && jornadas.length === 0 ? (
        <p className="muted" data-no-toast>Esta categoría todavía no tiene jornadas.</p>
      ) : (
        <ol className="jornada-dates__list">
          {jornadas.map((row) => {
            const saved = savedWindow(row);
            const draft = draftFor(row);
            const complete = Boolean(draft.starts && draft.ends);
            const invalid = complete && draft.ends < draft.starts;
            const dirty = saved === null ? complete : draft.starts !== saved.starts || draft.ends !== saved.ends;
            const showingSuggestion = saved === null && drafts[row.round_number] === undefined && suggestedWindow(row) !== null;
            const effectiveStart = saved?.starts ?? row.suggested_starts_on;
            const shiftDays = effectiveStart && draft.starts ? daysBetween(effectiveStart, draft.starts) ?? 0 : 0;
            const hasFollowing = jornadas.some((other) => other.round_number > row.round_number && other.can_save);
            const rowEditable = editable && row.can_save;
            const rowBusy = busy !== null;
            const savedRange = saved ? jornadaRangeText(saved.starts, saved.ends) : null;
            return (
              <li key={row.round_number} className="jornada-dates__row" data-jornada={row.round_number}>
                <div className="jornada-dates__name">
                  <strong>{row.label}</strong>
                  <small>{plural(row.matches_count, "partido", "partidos")}</small>
                </div>
                <div className="jornada-dates__fields">
                  <label>
                    <span>Del</span>
                    <input
                      type="date"
                      value={draft.starts}
                      onChange={(event) => updateDraft(row, { starts: event.target.value })}
                      disabled={!rowEditable || rowBusy}
                      aria-label={`${row.label}: desde`}
                    />
                  </label>
                  <label>
                    <span>al</span>
                    <input
                      type="date"
                      value={draft.ends}
                      min={draft.starts || undefined}
                      onChange={(event) => updateDraft(row, { ends: event.target.value })}
                      disabled={!rowEditable || rowBusy}
                      aria-label={`${row.label}: hasta`}
                    />
                  </label>
                </div>
                <div className="jornada-dates__state" data-no-toast>
                  {invalid ? (
                    <small className="jornada-dates__invalid" role="alert">
                      La fecha final no puede ser antes de la fecha de inicio.
                    </small>
                  ) : !row.can_save ? (
                    <small className="muted">Esta jornada todavía no está armada: créala en la sección de jornadas.</small>
                  ) : showingSuggestion ? (
                    <small className="muted" data-jornada-suggestion>
                      Sugerencia del formato: aún no se guarda.
                    </small>
                  ) : saved === null && !complete ? (
                    <small className="muted">Sin fechas.</small>
                  ) : savedRange && !dirty ? (
                    <small className="muted">Guardada: {savedRange}.</small>
                  ) : null}
                </div>
                {rowEditable ? (
                  <div className="jornada-dates__actions">
                    <button
                      type="button"
                      className="btn btn--compact"
                      onClick={() => void handleSave(row, false)}
                      disabled={rowBusy || !complete || invalid || !dirty}
                    >
                      {busy === row.round_number ? "Guardando…" : "Guardar"}
                    </button>
                    {hasFollowing && complete && !invalid && shiftDays !== 0 ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--compact"
                        onClick={() => void handleSave(row, true)}
                        disabled={rowBusy}
                      >
                        Guardar y recorrer las siguientes ({shiftLabel(shiftDays)})
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {renderRowExtra ? <div className="jornada-dates__extra">{renderRowExtra(row.round_number)}</div> : null}
              </li>
            );
          })}
        </ol>
      )}

      {actionError ? (
        <p className="form-error" role="alert">
          {actionError}
        </p>
      ) : null}
      {message ? <p className="form-message">{message}</p> : null}

      {editable && data && (data.other_divisions_count ?? 0) > 0 && hasSavedDates ? (
        <div className="jornada-dates__copy">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void handleCopyToAll()}
            disabled={busy !== null}
          >
            {busy === "copy" ? "Copiando…" : "Usar estas fechas en todas las categorías"}
          </button>
          <small className="muted" data-no-toast>
            Copia las fechas guardadas de cada jornada a la misma jornada de las otras categorías (solo donde exista).
          </small>
        </div>
      ) : null}
    </section>
  );
}
